// CarnetDialog — ver, imprimir, reemitir y suspender el carnet de un afiliado. El carnet lleva un
// código público único; el QR abre la página pública /validar-carnet/{codigo}, que confirma si es
// válido sin necesidad de iniciar sesión.

import React, { useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { QRCodeSVG } from 'qrcode.react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import FormularioDialog from './FormularioDialog';
import useCargar from '../hooks/useCargar';
import { afiliacionApi } from '../services/coopnetApi';
import { fecha } from '../utils/formato';

const COLOR = { activo: '#2E7D32', suspendido: '#E65100', revocado: '#616161', vencido: '#C62828' };
const urlVerificacion = (codigo) => `${window.location.origin}/validar-carnet/${codigo}`;
const esc = (t) => String(t ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function imprimirCarnet(c) {
  const qr = renderToStaticMarkup(<QRCodeSVG value={urlVerificacion(c.codigo)} size={130} />);
  const w = window.open('', '_blank', 'width=600,height=440');
  if (!w) {
    window.alert('El navegador bloqueó la ventana de impresión. Permite las ventanas emergentes para este sitio e inténtalo de nuevo.');
    return;
  }
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Carnet ${esc(c.numeroRegistro)}</title>
  <style>
    body{font-family:Arial,sans-serif;margin:24px;color:#1b1b1b}
    .carnet{width:440px;border:2px solid #2E7D32;border-radius:14px;overflow:hidden}
    .cab{background:#2E7D32;color:#fff;padding:10px 16px;font-weight:700;font-size:15px;display:flex;justify-content:space-between}
    .cuerpo{display:flex;justify-content:space-between;gap:14px;padding:16px}
    .nombre{font-size:20px;font-weight:700;text-transform:uppercase}
    .dato{font-size:13px;margin-top:6px} .peq{font-size:11px;color:#555}
  </style></head><body>
  <div class="carnet"><div class="cab"><span>${esc(c.cooperativa)}</span><span>CARNET</span></div>
  <div class="cuerpo"><div>
    <div class="nombre">${esc(c.afiliado)}</div>
    <div class="dato">${esc(c.tipoDocumento)} ${esc(c.documento)}</div>
    <div class="dato">N.º de registro: <b>${esc(c.numeroRegistro)}</b></div>
    <div class="dato">Expedido: ${esc(fecha(c.fechaExpedicion))} · Vence: <b>${esc(fecha(c.fechaVigencia))}</b></div>
    <div class="peq" style="margin-top:10px">Verifica este carnet escaneando el código QR.</div>
  </div><div>${qr}</div></div></div>
  <script>setTimeout(function(){window.print()},300)</script></body></html>`);
  w.document.close();
}

function Tarjeta({ carnet }) {
  const color = COLOR[carnet.estado] || COLOR.activo;
  return (
    <Box sx={{ border: `2px solid ${color}`, borderRadius: 3, overflow: 'hidden', maxWidth: 460, mx: 'auto' }}>
      <Box sx={{ bgcolor: color, color: 'white', px: 2, py: 1.2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography fontWeight={700} color="inherit">{carnet.cooperativa}</Typography>
        <Typography fontWeight={700} color="inherit">CARNET</Typography>
      </Box>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ textTransform: 'uppercase' }}>{carnet.afiliado}</Typography>
          <Typography variant="body2">{carnet.tipoDocumento} {carnet.documento}</Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>N.º de registro: <b>{carnet.numeroRegistro}</b></Typography>
          <Typography variant="body2">Expedido: {fecha(carnet.fechaExpedicion)}</Typography>
          <Typography variant="body2">Vence: <b>{fecha(carnet.fechaVigencia)}</b></Typography>
          <Chip size="small" sx={{ mt: 1.5, bgcolor: color, color: 'white', textTransform: 'capitalize' }} label={carnet.estado} />
        </Box>
        <QRCodeSVG value={urlVerificacion(carnet.codigo)} size={120} />
      </Box>
    </Box>
  );
}

/**
 * @param {{id: string, nombre?: string, apellido?: string}|null} afiliado  null = cerrado
 * @param {() => void} onClose
 * @param {() => void} [onCambio]  se llama cuando cambia algo (para refrescar el listado)
 */
export default function CarnetDialog({ afiliado, onClose, onCambio }) {
  const { datos: carnet, cargando, error, recargar } = useCargar(() => (afiliado ? afiliacionApi.carnet(afiliado.id) : null), [afiliado?.id]);
  const [dlg, setDlg] = useState(null);
  const [errorAccion, setErrorAccion] = useState('');

  const cambio = () => { recargar(); onCambio?.(); };
  const emitir = async () => {
    setErrorAccion('');
    try { await afiliacionApi.emitirCarnet(afiliado.id); cambio(); } catch (e) { setErrorAccion(e.message); }
  };
  const reemitir = () => {
    if (window.confirm('¿Emitir un carnet nuevo? El actual quedará revocado y dejará de ser válido.')) emitir();
  };

  return (
    <Dialog open={Boolean(afiliado)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Carnet {afiliado ? `— ${afiliado.nombre ?? ''} ${afiliado.apellido ?? ''}` : ''}</DialogTitle>
      <DialogContent dividers>
        {(error || errorAccion) && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorAccion('')}>{errorAccion || error}</Alert>}
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
        ) : carnet ? (
          <>
            <Tarjeta carnet={carnet} />
            {carnet.motivoEstado && <Alert severity="warning" sx={{ mt: 2 }}>{carnet.motivoEstado}</Alert>}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, textAlign: 'center' }}>
              Código de verificación: <b>{carnet.codigo}</b>
            </Typography>
          </>
        ) : (
          <Alert severity="info">Este afiliado aún no tiene carnet. Se emite al registrar el pago de la inscripción; también puedes emitirlo aquí si ya está activo.</Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        {carnet ? (
          <>
            <Button startIcon={<PrintIcon />} onClick={() => imprimirCarnet(carnet)}>Imprimir</Button>
            <Button onClick={reemitir}>Reemitir</Button>
            {carnet.estado === 'activo' && <Button color="warning" onClick={() => setDlg('suspender')}>Suspender</Button>}
            {carnet.estado === 'suspendido' && <Button color="success" onClick={async () => { try { await afiliacionApi.cambiarEstadoCarnet(carnet.id, 'activo'); cambio(); } catch (e) { setErrorAccion(e.message); } }}>Reactivar</Button>}
          </>
        ) : (
          !cargando && <Button variant="contained" onClick={emitir}>Emitir carnet</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>

      <FormularioDialog
        abierto={dlg === 'suspender'}
        titulo="Suspender carnet"
        descripcion="Mientras esté suspendido, la verificación pública lo mostrará como no válido."
        campos={[{ campo: 'motivo', label: 'Motivo', tipo: 'area', requerido: true }]}
        textoBoton="Suspender"
        onGuardar={async (v) => { await afiliacionApi.cambiarEstadoCarnet(carnet.id, 'suspendido', v.motivo); cambio(); }}
        onClose={() => setDlg(null)}
      />
    </Dialog>
  );
}
